import React, { useState, useEffect, useRef } from 'react';
import { CartItem } from '../types';
import { useModernNotification } from '../hooks/useModernNotification';
import { useTranslation } from 'react-i18next';
import { createDineInOrder } from '../services/dineInOrderService';
import { createTakeawayOrder } from '../services/takeawayOrderService';
import { OrderConfirmation } from './OrderConfirmation';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Lottie from 'lottie-react';
import leftHandAnimation from '../a/lotie1.json';
import { Menu, ShoppingBag, Settings, AlertCircle, X, Trash2, User, CreditCard, Wallet, Minus, Plus, Delete } from 'lucide-react';
import { getStoreInfo } from '../services/api';  

// Initialiser Stripe avec votre clé publique
const stripePromise = loadStripe('pk_test_51RZKIIQ3EDovu3Q4ECdxB8CCxpdwx6Jt5MMiZzkByaFyknW2Sa5BKDkDKVhue1PdWzoS16ecEDTkGa237WJI1WCB00D1jMSu3Q'); // Remplacez par votre clé publique

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (uniqueId: string, quantity: number) => void;
  onRemoveItem: (uniqueId: string) => void;
  onClearCart?: () => void;
  onCheckout: () => void;
  printSettings?: 'manual' | 'auto' | 'none';
} 

interface CartItem {
  menuItem: {
    id: string;
    name: string;
    price: number;
    description?: string;
    image?: string;
  };
  quantity: number;
  customizations: { [key: string]: { id: string; name: string; price: number }[] };
  uniqueId: string;
}

const calculateItemPrice = (item: CartItem) => {
  const basePrice = item.menuItem.price;
  const optionsPrice = Object.entries(item.customizations).reduce((acc, [_, options]) => {
    return acc + options.reduce((sum, opt) => sum + (opt.price || 0), 0);
  }, 0);
  const unitPrice = basePrice + optionsPrice;
  const totalPrice = unitPrice * item.quantity;
  return { unitPrice, totalPrice };
};

const getAdjustedTotal = (items: CartItem[]): number => {
  return items.reduce((sum, item) => {
    const { totalPrice } = calculateItemPrice(item);
    return sum + totalPrice;
  }, 0);
};

// Composant pour le formulaire de paiement Stripe
const StripePaymentForm: React.FC<{
  amount: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  onCancel: () => void;
}> = ({ amount, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Appeler le backend pour créer un PaymentIntent
   const response = await fetch('http://localhost:3001/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amount * 100 }), // Montant en centimes
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la création du PaymentIntent');
      }

      const { clientSecret } = await response.json();

      // Confirmer le paiement avec Stripe
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: complementaryInfo || 'Client',
          },
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message || t('Erreur lors du paiement'));
        onError(result.error.message || t('Erreur lors du paiement'));
      } else if (result.paymentIntent?.status === 'succeeded') {
        onSuccess();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('Erreur inconnue');
      setErrorMessage(message);
      onError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CardElement
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }}
        className="p-3 border border-gray-200 rounded-lg"
      />
      {errorMessage && (
        <div className="text-red-500 text-sm">{errorMessage}</div>
      )}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isProcessing || !stripe || !elements}
          className="flex-1 py-3 rounded-xl font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
        >
          {isProcessing ? t('Traitement...') : t('Payer')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 rounded-xl font-semibold text-red-500 bg-red-50 hover:bg-red-100"
        >
          {t('Annuler')}
        </button>
      </div>
    </form>
  );
};

export const Cart: React.FC<CartProps> = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  printSettings = 'manual',
  onCheckout,
}) => {
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [orderType, setOrderType] = useState<'takeaway' | 'delivery' | 'dine-in' | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | null>(null);
  const [customerName, setCustomerName] = useState<string>('Nom non indiqué');
  const [tableNumber, setTableNumber] = useState<string>('1');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [complementaryInfo, setComplementaryInfo] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [displayCustomerName, setDisplayCustomerName] = useState<string>('');
  const [displayTableNumber, setDisplayTableNumber] = useState<string>('');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showLetterKeyboard, setShowLetterKeyboard] = useState(false);
  const [showNumberKeyboard, setShowNumberKeyboard] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [itemNotes, setItemNotes] = useState<{ [key: string]: string }>({});
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [showNoteKeyboard, setShowNoteKeyboard] = useState(false);
  const [activeField, setActiveField] = useState<'name' | 'table' | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentStep, setCurrentStep] = useState<'type' | 'info' | 'payment'>('type');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPaymentProcessor, setShowPaymentProcessor] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [adjustedTotal, setAdjustedTotal] = useState<number | null>(null);
  const [invoiceData, setInvoiceData] = useState<any | null>(null);
  const [cartResult, setCartResult] = useState<any | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

const [deliveryCoordinates, setDeliveryCoordinates] = useState<{ lat: number; lng: number } | null>(null);



  
  const [paymentSettings] = useState(() => {
    const stored = localStorage.getItem('paymentSettings');
    return stored ? JSON.parse(stored) : { enableCardPayment: false, enableCashPayment: true };
  });

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const isEmpty = totalItems === 0;
  const total = getAdjustedTotal(items);
  const { t } = useTranslation();
  const notification = useModernNotification();

useEffect(() => {
  if (orderType === 'delivery' && window.google?.maps?.places && addressInputRef.current) {
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'fr' },
      fields: ['formatted_address', 'geometry.location'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.formatted_address && place.geometry?.location) {
        setDeliveryAddress(place.formatted_address);
        setDeliveryCoordinates({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        console.log('[Cart] Autocomplétion:', {
          address: place.formatted_address,
          lat: place.geometry.location.lat(), 
          lng: place.geometry.location.lng(),
        });
      } else {
        notification.showNotification({
          title: t('Erreur'),
          message: t('Adresse invalide ou incomplète'),
          type: 'error',
        });
      }
    }); 
  } 
}, [orderType, t, notification]);

  useEffect(() => {
    const checkScroll = () => {
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        setShowScrollIndicator(scrollTop + clientHeight < scrollHeight - 1);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScroll);
      setTimeout(checkScroll, 100);
    }

    return () => {
      if (scrollContainer) scrollContainer.removeEventListener('scroll', checkScroll);
    };
  }, [items]);

  useEffect(() => {
    setAdjustedTotal(null);
  }, [items]);

  const handleQuantityUpdate = (uniqueId: string, newQuantity: number) => {
    if (!uniqueId) {
      console.error('Erreur: uniqueId est undefined dans handleQuantityUpdate');
      return;
    }
    if (newQuantity < 1) {
      handleRemoveItem(uniqueId);
      return;
    }
    onUpdateQuantity(uniqueId, newQuantity);
  };

  const handleRemoveItem = (uniqueId: string) => {
    if (!uniqueId) {
      console.error('Erreur: uniqueId est undefined dans handleRemoveItem');
      return;
    }
    onRemoveItem(uniqueId);
  };

  const clearCart = () => {
    if (onClearCart) {
      onClearCart();
    } else {
      items.forEach((item) => onRemoveItem(item.uniqueId));
    }
  };

  const resetOrderModal = () => {
    setOrderType(null);
    setPaymentMethod(null);
    setCustomerName('Nom non indiqué');
    setTableNumber('1');
    setDeliveryAddress('');
    setComplementaryInfo('');
    setPhoneNumber('');
    setFormErrors({});
    setDisplayCustomerName('');
    setDisplayTableNumber('');
    setShowOrderForm(false);
    setShowPaymentForm(false);
    setShowLetterKeyboard(false);
    setShowNumberKeyboard(false);
    setActiveField(null);
    setSubmitError(null);
    setCurrentStep('type');
    setShowPaymentProcessor(false);
    setAdjustedTotal(null);
    setCartResult(null);
    setInvoiceData(null);
  };

  const dismissOrderModal = () => {
    setShowOrderTypeModal(false);
  };

  const closeOrderModal = () => {
    setShowOrderTypeModal(false);
    resetOrderModal();
    clearCart();
  };

  const handleOrderTypeSelection = async (type: 'takeaway' | 'delivery' | 'dine-in') => {
    setIsTransitioning(true);
    setOrderType(type);
    requestAnimationFrame(() => {
      setCurrentStep('info');
      setShowOrderForm(true);
      setShowPaymentForm(false);
      if (type === 'takeaway') {
        setActiveField('name');
        setShowLetterKeyboard(true);
        setShowNumberKeyboard(false);
      } else if (type === 'delivery') {
  setActiveField(null);
  setShowLetterKeyboard(false);
  setShowNumberKeyboard(false);
  notification.showNotification({
    title: t('Localisation'),
    message: t('Détection de votre position GPS en cours...'),
    type: 'info',
  });
  handleUseCurrentLocation();
} else {
        setActiveField('table');
        setShowNumberKeyboard(true);
        setShowLetterKeyboard(false);
      }
      setIsTransitioning(false);
    });
  };

  const getPaymentMethodLabel = (method: string | null) => {
    switch (method) {
      case 'cash':
        return 'Paiement à la livraison';
      case 'card':
        return 'Carte';
      default:
        return 'Non spécifié';
    }
  };

  const handlePaymentMethodSelection = async (method: 'cash' | 'card') => {
    const paymentLabel = getPaymentMethodLabel(method);
    setPaymentMethod(paymentLabel);

    if (method === 'card') {
      if (!orderType) {
        setSubmitError(t('Veuillez sélectionner un type de commande avant de payer par carte'));
        setPaymentMethod(null);
        return;
      }
      setShowPaymentProcessor(true);
      setCurrentStep('payment');
      setShowPaymentForm(true);
      setShowOrderForm(false);
    } else {
      const paymentNote = 'Paiement à la livraison';
      setItemNotes((prev) => ({ ...prev, payment_method: paymentNote }));
      await handleSubmit();
      setShowConfirmation(true);
    }
  };

  const handlePaymentSuccess = () => {
    const paymentNote = 'Paiement par carte effectué';
    setItemNotes((prev) => ({ ...prev, payment_method: paymentNote }));
    setShowPaymentProcessor(false);
    setPaymentMethod(getPaymentMethodLabel('card'));
    handleSubmit();
    setShowConfirmation(true);
  };

  const handlePaymentCancel = () => {
    setPaymentMethod(null);
    setShowPaymentProcessor(false);
    setSubmitError(t('Paiement annulé. Veuillez choisir un mode de paiement pour réessayer ou annuler la commande.'));
    setCurrentStep('payment');
    setIsSubmitting(false);
  };

  const handlePaymentError = (error: string) => {
    setSubmitError(
      t('Erreur de paiement : {{error}}. Veuillez réessayer ou choisir un autre mode de paiement.', { error })
    );
    setPaymentMethod(null);
    setShowPaymentProcessor(false);
    setCurrentStep('payment');
    setIsSubmitting(false);
  };
  
const handleUseCurrentLocation = async () => {
  if (!navigator.geolocation) { 
    notification.showNotification({
      title: t('Erreur'),
      message: t('La géolocalisation n’est pas prise en charge par votre navigateur'),
      type: 'error',
    });
    return;
  }

  // Vérifier le cache
  const cachedLocation = localStorage.getItem('lastLocation');
  if (cachedLocation) { 
    const { address, lat, lng, timestamp } = JSON.parse(cachedLocation);
    const age = Date.now() - timestamp;
    if (age < 5 * 60 * 1000) { // 5 minutes
      setDeliveryAddress(address);
      setDeliveryCoordinates({ lat, lng });
      notification.showNotification({
        title: t('Succès'),
        message: t('Position chargée depuis le cache'),
        type: 'success',
      });
      console.log('[Cart] Utilisation du cache:', { address, lat, lng, age });
      return;
    }
  }

  const attemptGeolocation = async (retries = 2, minAccuracy = 100) => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          console.log('[Cart] Coordonnées obtenues:', {
            latitude,
            longitude,
            accuracy,
            source: position.coords.altitude ? 'GPS probable' : 'Internet probable',
          });

          // Assouplir la précision pour les retries
          if (accuracy > minAccuracy && retries > 0) {
            console.warn('[Cart] Précision faible:', { accuracy, retries, minAccuracy });
            notification.showNotification({
              title: t('Avertissement'),
              message: t('Position peu précise. Nouvelle tentative...'),
              type: 'warning',
            });
            setTimeout(() => attemptGeolocation(retries - 1, minAccuracy * 2).then(resolve).catch(reject), 1000);
            return;
          }

          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyBIq-i9Mdr5w5sj00_IEFPVazTr2T9bVYc`
            );
            if (!response.ok) {
              console.error('[Cart] Erreur réseau Geocoding:', response.status, await response.text());
              throw new Error(`Erreur réseau: ${response.status}`);
            }
            const data = await response.json();
            console.log('[Cart] Réponse Geocoding API:', JSON.stringify(data, null, 2));
            if (data.status === 'OK' && data.results[0]) {
              const address = data.results[0].formatted_address;
              setDeliveryAddress(address);
              setDeliveryCoordinates({ lat: latitude, lng: longitude });
              // Mettre en cache
              localStorage.setItem('lastLocation', JSON.stringify({
                address,
                lat: latitude,
                lng: longitude,
                timestamp: Date.now(),
              }));
              notification.showNotification({
                title: t('Succès'),
                message: t('Position GPS détectée avec succès (précision: {{accuracy}}m)', {
                  accuracy: Math.round(accuracy),
                }),
                type: 'success',
              });
              console.log('[Cart] Géolocalisation réussie:', { latitude, longitude, address, accuracy });
              resolve(address);
            } else {
              console.warn('[Cart] Échec Geocoding:', {
                status: data.status,
                error_message: data.error_message,
                results: data.results,
              });
              notification.showNotification({
                title: t('Erreur'),
                message: data.error_message
                  ? t('Erreur API: {{message}}', { message: data.error_message })
                  : t('Aucune adresse trouvée pour cette position. Veuillez saisir une adresse manuellement.'),
                type: 'error',
              });
              reject(new Error('Aucune adresse trouvée'));
            }
          } catch (error) {
            notification.showNotification({
              title: t('Erreur'),
              message: t('Impossible de récupérer l’adresse à partir de votre position'),
              type: 'error',
            });
            console.error('[Cart] Erreur géolocalisation:', error);
            reject(error);
          }
        },
        (error) => {
          let message = t('Erreur de géolocalisation');
          if (error.code === error.PERMISSION_DENIED) {
            message = t('Vous avez refusé l’accès à votre position');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            message = t('Position GPS non disponible. Vérifiez que le GPS est activé et que vous êtes en extérieur.');
          } else if (error.code === error.TIMEOUT) {
            message = t('Délai d’attente dépassé pour obtenir la position GPS.');
            if (retries > 0) {
              console.warn('[Cart] Timeout GPS, nouvelle tentative:', { retries });
              setTimeout(() => attemptGeolocation(retries - 1, minAccuracy * 2).then(resolve).catch(reject), 1000);
              return;
            }
          }
          notification.showNotification({
            title: t('Erreur'),
            message,
            type: 'error',
          });
          console.error('[Cart] Erreur géolocalisation HTML5:', error);
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  try {
    await attemptGeolocation();
  } catch (error) {
    console.error('[Cart] Échec final de la géolocalisation après tentatives');
  }
};
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let response;
      if (orderType === 'dine-in') {
        console.log('[Cart] Appel de createDineInOrder avec:', {
          items,
          tableNumber,
          customerName,
          total: adjustedTotal || total,
          itemNotes,
          cartResult,
          invoiceData,
        });
        response = await createDineInOrder(
          items,
          tableNumber,
          customerName,
          adjustedTotal || total,
          itemNotes,
          cartResult,
          invoiceData
        );
      } else {
        console.log('[Cart] Appel de createTakeawayOrder avec:', {
          items,
          customerName: orderType === 'delivery' ? complementaryInfo || 'Anonyme' : customerName,
          total: adjustedTotal || total,
          itemNotes,
          cartResult,
          invoiceData,
        });
        response = await createTakeawayOrder(
          items,
          orderType === 'delivery' ? complementaryInfo || 'Anonyme' : customerName,
          adjustedTotal || total,
          itemNotes,
          cartResult,
          invoiceData
        );
      }

      console.log('Réponse create_order:', JSON.stringify(response, null, 2));

      if (response.success) { 
        setAdjustedTotal(response.displayTotal);
        setInvoiceData((prev) => {
          const newInvoiceData = {
            ...prev,
            ...response,
            order_id: response.order_id || prev?.order_payment?._id,
          };
          console.log('invoiceData après create_order:', JSON.stringify(newInvoiceData, null, 2));
          return newInvoiceData;
        });
        setShowConfirmation(true);
        setShowOrderTypeModal(false);
        notification.showNotification({
          title: t('Succès'),
          message: t('Votre commande a été enregistrée avec succès !'),
          type: 'success',
        });
      } else {
        console.warn('[Cart] Réponse non réussie:', response.message);
        setAdjustedTotal(response.displayTotal || total);
        setSubmitError(response.message || t('Une erreur est survenue lors de la commande'));
        notification.showNotification({
          title: t('Erreur'),
          message: response.message || t('Une erreur est survenue lors de la commande'),
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Erreur lors de la soumission de la commande:', error);
      setSubmitError(error instanceof Error ? error.message : t('Une erreur est survenue'));
      setAdjustedTotal(total);
      notification.showNotification({
        title: t('Erreur'),
        message: error instanceof Error ? error.message : t('Une erreur est survenue'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setItemNotes({});
    setCustomerName('Nom non indiqué');
    setTableNumber('1');
    setDeliveryAddress('');
    setComplementaryInfo('');
    setPhoneNumber('');
    setFormErrors({});
    setDisplayCustomerName('');
    setDisplayTableNumber('');
    setOrderType(null);
    setPaymentMethod(null);
    setShowOrderTypeModal(false);
    setAdjustedTotal(null);
    setCartResult(null);
    setInvoiceData(null);
    clearCart();
    onCheckout();
  };

  const handleFieldFocus = (field: 'name' | 'table') => {
    setActiveField(field);
    if (field === 'name') {
      setShowLetterKeyboard(true);
      setShowNumberKeyboard(false);
    } else {
      setShowLetterKeyboard(false);
      setShowNumberKeyboard(true);
    }
  };

  const handleNumberPress = (number: string) => {
    if (number === 'backspace') {
      setTableNumber((prev) => {
        const newValue = prev.slice(0, -1);
        setDisplayTableNumber(newValue);
        return newValue;
      });
      return;
    }
    if (number === 'clear') {
      setTableNumber('');
      setDisplayTableNumber('');
      return;
    }
    if (tableNumber.length < 3) {
      setTableNumber((prev) => {
        const newValue = prev + number;
        setDisplayTableNumber(newValue);
        return newValue;
      });
    }
  };

  const handleLetterPress = (letter: string) => {
    if (letter === 'backspace') {
      setCustomerName((prev) => {
        const newValue = prev === 'Nom non indiqué' ? '' : prev.slice(0, -1);
        setDisplayCustomerName(newValue);
        return newValue;
      });
      return;
    }
    if (letter === 'clear') {
      setCustomerName('');
      setDisplayCustomerName('');
      return;
    }
    if (letter === 'space') {
      setCustomerName((prev) => {
        const newValue = prev === 'Nom non indiqué' ? ' ' : prev + ' ';
        setDisplayCustomerName(newValue);
        return newValue;
      });
      return;
    }
    if (customerName.length < 30 || customerName === 'Nom non indiqué') {
      setCustomerName((prev) => {
        const newValue = prev === 'Nom non indiqué' ? letter : prev + letter;
        setDisplayCustomerName(newValue);
        return newValue;
      });
    }
  };

  const handleNoteChange = (uniqueId: string, note: string) => {
    setItemNotes((prev) => ({ ...prev, [uniqueId]: note }));
  };

  const handleNoteKeyPress = (letter: string) => {
    if (!activeNoteId) return;
    if (letter === 'backspace') {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: (prev[activeNoteId] || '').slice(0, -1) }));
      return;
    }
    if (letter === 'clear') {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: '' }));
      return;
    }
    if (letter === 'space') {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: (prev[activeNoteId] || '') + ' ' }));
      return;
    }
    if ((itemNotes[activeNoteId] || '').length < 200) {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: (prev[activeNoteId] || '') + letter }));
    }
  };

  const openNoteKeyboard = (uniqueId: string) => {
    setActiveNoteId(uniqueId);
    setShowNoteKeyboard(true);
  };

  const closeNoteKeyboard = () => {
    setActiveNoteId(null);
    setShowNoteKeyboard(false);
  };

  const letterRows = [
    ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
    ['W', 'X', 'C', 'V', 'B', 'N'],
  ];

  const numberRows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'backspace'],
  ];

  const handleNextStep = async () => {
    setIsTransitioning(true);
    requestAnimationFrame(async () => {
      if (currentStep === 'type') {
        setCurrentStep('info');
        setShowOrderForm(true);
        setShowPaymentForm(false);
        setIsTransitioning(false);
      } else if (currentStep === 'info') {
        if (orderType === 'delivery') {
          const errors: { [key: string]: string } = {};
          if (!deliveryAddress.trim()) {
            errors.address = t('L’adresse de livraison est requise');
          }
          if (!phoneNumber.match(/^\+?\d{10,15}$/)) {
            errors.phoneNumber = t('Numéro de téléphone invalide');
          }
          setFormErrors(errors);
          if (Object.keys(errors).length > 0) {
            setIsTransitioning(false);
            notification.showNotification({
              title: t('Erreur'),
              message: t('Veuillez corriger les erreurs dans le formulaire'),
              type: 'error',
            });
            return;
          }
        } else if (orderType === 'dine-in' && !tableNumber.trim()) {
          setSubmitError(t('Veuillez entrer un numéro de table'));
          setIsTransitioning(false);
          return;
        }

        if (!items.length) {
          setSubmitError(t('Le panier est vide. Veuillez ajouter des produits.'));
          setIsTransitioning(false);
          return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        const storeInfo = getStoreInfo();
        if (!storeInfo || !storeInfo.store_id || !storeInfo.server_token) {
          console.error('[Cart] Informations du store non disponibles:', storeInfo);
          setSubmitError(t('Impossible de passer la commande : informations du magasin non disponibles.'));
          notification.showNotification({
            title: t('Erreur'),
            message: t('Informations du magasin non disponibles. Veuillez réessayer ou contacter le support.'),
            type: 'error',
          });
          setIsSubmitting(false);
          setIsTransitioning(false);
          return;
        }

        console.log('[Cart] storeInfo:', {
          store_id: storeInfo.store_id,
          is_tax_included: storeInfo.is_tax_included,
          is_use_item_tax: storeInfo.is_use_item_tax,
          server_token: storeInfo.server_token,
        });

        try {
          let formattedCustomerName;
          if (orderType === 'delivery') {
            const parts = [];
            if (complementaryInfo.trim()) parts.push(complementaryInfo.trim());
            if (deliveryAddress.trim()) parts.push(deliveryAddress.trim());
            if (phoneNumber.trim()) parts.push(phoneNumber.trim());
            const uniqueParts = [...new Set(parts)];
            const infoString = uniqueParts.join(' - ') || 'Anonyme';
            formattedCustomerName = `website - ${infoString}`;
          } else {
            const baseName = customerName.trim() && customerName !== 'Nom non indiqué' ? customerName.trim() : 'Anonyme';
            formattedCustomerName = `website - ${baseName}`;
          }

          const addressParts = storeInfo.address.split(',');
          const city = addressParts.length > 1 ? addressParts[1].trim() : '';

          if (orderType === 'delivery') {
            const deliveryDetails = {
              address: deliveryAddress,
              complementaryInfo: complementaryInfo || '',
              phoneNumber,
              orderType: 'delivery',
              timestamp: new Date().toISOString(),
            };
            const storedDeliveryOrders = JSON.parse(localStorage.getItem('deliveryOrders') || '[]');
            storedDeliveryOrders.push(deliveryDetails);
            localStorage.setItem('deliveryOrders', JSON.stringify(storedDeliveryOrders));
            console.log('[Cart] Informations de livraison enregistrées localement:', deliveryDetails);
          }

          const handleTaxNotIncludedAndItemTaxFalse = async () => {
            let totalHT = 0;

            const itemsWithTax = items.map((item) => {
              const itemPrice = item.menuItem.price;
              const customizationPrice = Object.values(item.customizations).reduce(
                (sum, options) => sum + options.reduce((optSum, opt) => optSum + opt.price, 0),
                0
              );
              const totalItemPriceHT = (itemPrice + customizationPrice) * item.quantity;

              totalHT += totalItemPriceHT;

              return { ...item, totalItemPriceHT, itemTax: 0, itemTaxRate: 0, customizationPrice };
            });

            const cartData = {
              cart_id: '',
              cart_unique_token: storeInfo.server_token,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              destination_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'destination',
                  city: city || '',
                  delivery_status: 0,
                  location: storeInfo.location || { lat: 0, lng: 0 },
                  note: '',
                  user_details: {
                    country_phone_code: storeInfo.country_phone_code || '+33',
                    email: '',
                    first_name: '',
                    image_url: '',
                    last_name: '',
                    name: orderType === 'dine-in' ? `${formattedCustomerName} - Table ${tableNumber}` : formattedCustomerName,
                    phone: '',
                    user_type: 7,
                  },
                },
              ],
              id: storeInfo.store_id,
              is_guest_order: true,
              is_tax_included: false,
              is_use_item_tax: false,
              order_details: [
                {
                  items: itemsWithTax.map((item) => ({
                    item_id: item.menuItem.id,
                    unique_id: Math.floor(Math.random() * 1000),
                    item_name: item.menuItem.name,
                    quantity: item.quantity,
                    details: item.menuItem.description || '',
                    image_url: [item.menuItem.image || ''],
                    item_price: item.menuItem.price,
                    note_for_item: itemNotes[item.uniqueId] || '',
                    specifications: Object.entries(item.customizations).map(([specId, options]) => ({
                      list: options.map((opt) => ({
                        _id: `${item.menuItem.id}-${specId}-${opt.id}`,
                        main_specification_id: null,
                        franchise_id: null,
                        name: opt.name,
                        price: opt.price,
                        quantity: 1,
                        is_default_selected: false,
                        is_user_selected: true,
                      })),
                      name: specId,
                      price: options.reduce((sum, opt) => sum + opt.price, 0),
                      type: 1,
                    })),
                    tax: 0,
                    tax_details: [],
                    total_item_price: item.totalItemPriceHT,
                    total_item_tax: 0,
                    total_specification_tax: 0,
                    product_id: item.menuItem.id,
                    product_name: item.menuItem.name,
                  })),
                  product_id: items[0]?.menuItem.id || '',
                  product_name: items[0]?.menuItem.name || '',
                  total_item_price: totalHT,
                  total_item_tax: 0,
                  unique_id: Math.floor(Math.random() * 1000),
                },
              ],
              pickup_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'pickup',
                  delivery_status: 0,
                  location: storeInfo.location || { lat: 0, lng: 0 },
                  note: '',
                  user_details: {
                    name: storeInfo.name || 'Store',
                    email: storeInfo.email || '',
                    phone: storeInfo.phone || '',
                    country_phone_code: storeInfo.country_phone_code || '+33',
                    image_url: storeInfo.image_url || '',
                    user_type: 2,
                  },
                },
              ],
              store_id: storeInfo.store_id,
              table_no: orderType === 'dine-in' ? tableNumber : '0',
              timezone: 'Europe/Paris',
              total_cart_price: totalHT,
              total_item_tax: 0,
              user_id: '',
              user_type: 2,
            };

            console.log('[Cart] Envoi de cartData à add_item_in_cart:', JSON.stringify(cartData, null, 2));
            const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(cartData),
            });

            if (!cartResponse.ok) {
              const errorText = await cartResponse.text();
              console.error('[Cart] Erreur réseau add_item_in_cart:', cartResponse.status, errorText);
              throw new Error(t('Erreur lors de l’ajout au panier: {{status}}', { status: cartResponse.status }));
            }

            const cartResult = await cartResponse.json();
            console.log('[Cart] Réponse add_item_in_cart:', JSON.stringify(cartResult, null, 2));
            if (!cartResult.success) {
              console.error('[Cart] Échec add_item_in_cart:', cartResult.message);
              throw new Error(cartResult.message || t('Erreur lors de l’ajout au panier'));
            }

            const invoiceData = {
              cart_id: cartResult.cart_id || '',
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              id: storeInfo.store_id,
              is_guest_order: true,
              is_store_place_order: orderType === 'dine-in',
              is_tax_included: false,
              is_use_item_tax: false,
              is_user_pick_up_order: orderType !== 'dine-in',
              order_type: 2,
              store_id: storeInfo.store_id,
              timezone: 'Europe/Paris',
              total_cart_amout_without_tax: totalHT,
              total_cart_price: totalHT,
              total_distance: 0,
              total_item_count: items.length,
              total_item_tax: 0,
              total_time: 0,
              user_id: cartResult.user_id || '',
              table_no: orderType === 'dine-in' ? tableNumber : undefined,
            };

            console.log('[Cart] Envoi de invoiceData à get_order_cart_invoice:', JSON.stringify(invoiceData, null, 2));
            const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(invoiceData),
            });

            if (!invoiceResponse.ok) {
              const errorText = await invoiceResponse.text();
              console.error('[Cart] Erreur réseau get_order_cart_invoice:', invoiceResponse.status, errorText);
              throw new Error(t('Erreur lors de la vérification de la facture: {{status}}', { status: invoiceResponse.status }));
            }

            const invoiceResult = await invoiceResponse.json();
            console.log('[Cart] Réponse get_order_cart_invoice:', JSON.stringify(invoiceResult, null, 2));
            if (!invoiceResult.success) {
              console.error('[Cart] Échec get_order_cart_invoice:', invoiceResult.message);
              throw new Error(invoiceResult.message || t('Erreur lors de la vérification de la facture'));
            }

            return { cartResult, invoiceResult };
          };

          let result;
          if (storeInfo.is_tax_included && storeInfo.is_use_item_tax) {
            throw new Error('Configuration non supportée pour cette démo');
          } else if (storeInfo.is_tax_included && !storeInfo.is_use_item_tax) {
            throw new Error('Configuration non supportée pour cette démo');
          } else if (!storeInfo.is_tax_included && storeInfo.is_use_item_tax) {
            throw new Error('Configuration non supportée pour cette démo');
          } else {
            result = await handleTaxNotIncludedAndItemTaxFalse();
          }

          setCartResult(result.cartResult);
          setInvoiceData(result.invoiceResult);
          console.log('[Cart] invoiceData après get_order_cart_invoice:', JSON.stringify(result.invoiceResult, null, 2));
          const orderPayment = result.invoiceResult.order_payment;
          const finalTotal = orderPayment?.total_order_price || orderPayment?.total || total;
          setAdjustedTotal(finalTotal);

          const hasPaymentMethods = paymentSettings.enableCardPayment || paymentSettings.enableCashPayment;
          if (!hasPaymentMethods) {
            await handlePaymentMethodSelection('cash');
          } else {
            setCurrentStep('payment');
            setShowOrderForm(false);
            setShowPaymentForm(true);
          }
        } catch (error) {
          console.error('[Cart] Erreur dans handleNextStep:', error);
          setSubmitError(error instanceof Error ? error.message : t('Une erreur est survenue lors de la préparation de la commande'));
          notification.showNotification({
            title: t('Erreur'),
            message: error instanceof Error ? error.message : t('Une erreur est survenue lors de la préparation de la commande'),
            type: 'error',
          });
          setAdjustedTotal(total);
        } finally {
          setIsSubmitting(false);
          setIsTransitioning(false);
        }
      }
    });
  };

  const handlePreviousStep = () => {
    setIsTransitioning(true);
    requestAnimationFrame(() => {
      if (currentStep === 'payment') {
        setCurrentStep('info');
        setShowPaymentForm(false);
        setShowOrderForm(true);
      } else if (currentStep === 'info') {
        setCurrentStep('type');
        setShowOrderForm(false);
      }
      setIsTransitioning(false);
    });
  };

  console.log('Cart rendu - invoiceData avant OrderConfirmation:', JSON.stringify(invoiceData, null, 2));

  return (
    <Elements stripe={stripePromise}>
      <div className="bg-white rounded-2xl shadow-xl flex flex-col h-full max-h-[calc(100vh-100px)]">
        <div className="flex-shrink-0 p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">{t('Votre commande')}</h2>
            <span className="flex items-center gap-2 text-gray-500">
              <ShoppingBag size={18} />
              <span className="font-medium">{totalItems}</span>
            </span>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col relative">
          {isEmpty ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <ShoppingBag size={36} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium text-sm">{t('Votre panier est vide')}</p>
                <p className="text-gray-400 text-xs mt-1">{t('Veuillez sélectionner au moins 1 élément')}</p>
              </div>
            </div>
          ) : (
            <>
              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-3">
                {items.map((item) => {
                  const { unitPrice, totalPrice } = calculateItemPrice(item);

                  return (
                    <div
                      key={item.uniqueId}
                      className="flex flex-col gap-3 p-3 bg-gray-50 rounded-xl text-sm"
                    >
                      <div className="flex gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-800 truncate mb-1">{item.menuItem.name}</h3>
                          <div className="text-xs text-gray-500 mb-1">
                            {t('Prix unitaire:')} {unitPrice.toFixed(2)}
                          </div>
                          {Object.entries(item.customizations).map(([specId, options], optIndex) => (
                            <div key={`${item.uniqueId}-${specId}-${optIndex}`} className="text-xs text-gray-500">
                              {options.map((opt, i) => (
                                <span key={`${item.uniqueId}-${specId}-${opt.id}-${i}`}>
                                  {opt.name}
                                  {opt.price > 0 && ` (+${opt.price.toFixed(2)}€)`}
                                  {i < options.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          ))}
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => handleQuantityUpdate(item.uniqueId, item.quantity - 1)}
                              className="p-1 rounded-lg bg-white shadow-sm hover:bg-gray-100 transition-colors"
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={14} className="text-gray-600" />
                            </button>
                            <span className="font-medium text-gray-800">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityUpdate(item.uniqueId, item.quantity + 1)}
                              className="p-1 rounded-lg bg-white shadow-sm hover:bg-gray-100 transition-colors"
                            >
                              <Plus size={14} className="text-gray-600" />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          <span className="font-semibold text-gray-800">{totalPrice.toFixed(2)}€</span>
                          <button
                            onClick={() => handleRemoveItem(item.uniqueId)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div onClick={() => openNoteKeyboard(item.uniqueId)} className="relative cursor-text">
                          <textarea
                            placeholder={t('Ajouter une note...')}
                            value={itemNotes[item.uniqueId] || ''}
                            readOnly
                            className="w-full text-sm px-3 py-0 border border-gray-200 rounded-lg focus:ring-1 focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 resize-none bg-white cursor-text h-8 text-center leading-8"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {showScrollIndicator && (
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 pointer-events-none">
                  <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-400 opacity-75 animate-bounce" />
                </div>
              )}

              <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-white sticky bottom-0 z-10">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold text-gray-800">{t('Total')}</span>
                  <span className="text-lg font-bold text-orange-500">
                    {(adjustedTotal !== null ? adjustedTotal : total).toFixed(2)}€
                  </span>
                </div>
        <button
  onClick={() => {
    if (!showOrderTypeModal && !showConfirmation) {
      console.log('Ouverture du modal ordertype');
      setCurrentStep('type');
      setShowOrderTypeModal(true);
    }
  }}
  disabled={isEmpty}
  className="w-full py-3 rounded-xl font-semibold text-sm shadow-lg transition-colors bg-orange-500 text-white hover:bg-orange-600 shadow-orange-500/30"
>
  {t('Commander')}
</button>
              </div>
            </>
          )}
        </div>

        {showOrderTypeModal && (
<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1000 popup-overlay px-[3%] md:px-0">
            <div
              className={`bg-white rounded-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh] shadow-2xl p-6 relative transform transition-transform duration-300 ${
                isTransitioning ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
              }`}
            >
              <button
                onClick={dismissOrderModal}
                className="absolute top-0 right-0 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
              >
                <X size={24} />
              </button>

              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-500">
                  <span className={currentStep === 'type' ? 'font-bold text-orange-500' : ''}>
                    1. {t('Type de commande')}
                  </span>
                  <span className={currentStep === 'info' ? 'font-bold text-orange-500' : ''}>
                    2. {t('Informations')}
                  </span>
                  <span className={currentStep === 'payment' ? 'font-bold text-orange-500' : ''}>
                    3. {t('Paiement')}
                  </span>
                </div>
                <div className="h-1 bg-gray-200 rounded-full mt-2">
                  <div
                    className="h-1 bg-orange-500 rounded-full transition-all duration-300"
                    style={{
                      width: currentStep === 'type' ? '33%' : currentStep === 'info' ? '66%' : '100%',
                    }}
                  />
                </div>
              </div>

              {currentStep === 'type' && (
                <>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Mode de commande')}</h2>
                  <p className="text-gray-600 mb-6">{t('Veuillez sélectionner votre mode de commande')}</p>
                  <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-6">
                      <button
                        onClick={() => handleOrderTypeSelection('takeaway')}
                        className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                          orderType === 'takeaway'
                            ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                            : 'border-gray-200 hover:border-orange-200'
                        }`}
                      >
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <ShoppingBag size={24} className="text-orange-500" />
                        </div>
                        <span className="font-medium text-gray-800 text-lg">{t('À emporter')}</span>
                      </button>
                      <button
                        onClick={() => handleOrderTypeSelection('delivery')}
                        className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                          orderType === 'delivery'
                            ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                            : 'border-gray-200 hover:border-orange-200'
                        }`}
                      >
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <ShoppingBag size={24} className="text-orange-500" />
                        </div>
                        <span className="font-medium text-gray-800 text-lg">{t('Livraison')}</span>
                      </button>
                      <button
                        onClick={() => handleOrderTypeSelection('dine-in')}
                        className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                          orderType === 'dine-in'
                            ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                            : 'border-gray-200 hover:border-orange-200'
                        }`}
                      >
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <Menu size={24} className="text-orange-500" />
                        </div>
                        <span className="font-medium text-gray-800 text-lg">{t('Sur place')}</span>
                      </button>
                    </div>
                    <div className="flex justify-center mt-6">
                      <Lottie animationData={leftHandAnimation} loop={true} style={{ width: 300, height: 300 }} />
                    </div>
                  </div>
                </>
              )}

              {currentStep === 'info' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Entrez vos informations')}</h2>
                  {orderType === 'delivery' ? (
                    <>
                     <div>
  <div className="relative flex flex-col gap-2">
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <User size={20} className="text-gray-400" />
      </div>
     <input
  type="text"
  ref={addressInputRef}
  value={deliveryAddress}
  onChange={(e) => setDeliveryAddress(e.target.value)}
  onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
  placeholder={t('Adresse de livraison')}
  className={`w-full max-w-[95vw] pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-base font-semibold text-gray-800 bg-gray-50 ${
    formErrors.address ? 'border-red-500' : 'border-gray-200'
  }`}
/>  
    </div>
    <button
      onClick={handleUseCurrentLocation}
      className="w-full py-2 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
      aria-label={t('Utiliser ma position')}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      {t('Utiliser ma position')}
    </button>
    {formErrors.address && (
      <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>
    )}
  </div>
</div>
                      <div>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User size={20} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={complementaryInfo}
                            onChange={(e) => setComplementaryInfo(e.target.value)}
                            placeholder={t('Nom ou informations complémentaires (optionnel)')}
                            className="w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 border-gray-200"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User size={20} className="text-gray-400" />
                          </div>
                          <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder={t('Numéro de téléphone')}
                            className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 ${
                              formErrors.phoneNumber ? 'border-red-500' : 'border-gray-200'
                            }`}
                          />
                          {formErrors.phoneNumber && (
                            <p className="text-red-500 text-sm mt-1">{formErrors.phoneNumber}</p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : orderType === 'dine-in' ? (
                    <div>
                      <div onClick={() => handleFieldFocus('table')} className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Menu size={20} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={displayTableNumber}
                          readOnly
                          placeholder={t('Numéro de table')}
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 ${
                            activeField === 'table' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
                          }`}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div onClick={() => handleFieldFocus('name')} className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User size={20} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={displayCustomerName}
                          readOnly
                          placeholder={t('Votre nom', {
                            optional: orderType === 'takeaway' ? t('Optionnel') : '',
                          })}
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 ${
                            activeField === 'name' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
                          }`}
                        />
                      </div>
                    </div>
                  )}
               {(orderType === 'takeaway' || orderType === 'dine-in') && showNumberKeyboard && (
  <div className="mt-4 space-y-2">
    {numberRows.map((row, rowIndex) => (
      <div key={rowIndex} className="flex justify-center gap-2 px-2">
        {row.map((number) => (
          <button
            key={number}
            onClick={() => handleNumberPress(number)}
            className={`flex-1 max-w-[100px] h-16 rounded-lg text-lg font-semibold transition-all active:transform active:scale-95 ${
              number === 'backspace' || number === 'clear'
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200'
            }`}
          >
            {number === 'backspace' ? (
              <Delete size={20} className="mx-auto" />
            ) : number === 'clear' ? (
              t('Clear')
            ) : (
              number
            )}
          </button>
        ))}
      </div>
    ))}
  </div>
)}
{(orderType === 'takeaway' || orderType === 'dine-in') && showLetterKeyboard && (
  <div className="mt-4 space-y-2 px-2">
    {letterRows.map((row, rowIndex) => (
      <div key={rowIndex} className="flex justify-center gap-1 flex-wrap">
        {row.map((letter) => (
          <button
            key={letter}
            onClick={() => handleLetterPress(letter)}
            className="w-10 h-12 rounded-lg text-base font-semibold bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200 transition-all active:transform active:scale-95 flex-shrink-0"
          >
            {letter}
          </button>
        ))}
      </div>
    ))}
    <div className="flex justify-center gap-2 mt-2">
      <button
        onClick={() => handleLetterPress('clear')}
        className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all active:transform active:scale-95"
      >
        {t('Effacer')}
      </button>
      <button
        onClick={() => handleLetterPress('space')}
        className="px-6 py-2 rounded-lg text-sm font-semibold bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200 transition-all active:transform active:scale-95"
      >
        {t('Espace')}
      </button>
      <button
        onClick={() => handleLetterPress('backspace')}
        className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all active:transform active:scale-95"
      >
        <Delete size={20} />
      </button>
    </div>
  </div>
)}
                  <button
                    onClick={handleNextStep}
                    className="w-full mt-6 py-3 rounded-2xl font-semibold transition-colors bg-orange-500 text-white hover:bg-orange-600"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('Chargement') : t('Continuer vers le paiement')}
                  </button>
                </div>
              )}

              {currentStep === 'payment' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Mode de paiement')}</h2>
                  <p className="text-gray-600 mb-6">{t('Choisissez votre méthode de paiement préférée')}</p>
                  <div
                    className={`grid ${
                      (paymentSettings.enableCashPayment && !paymentSettings.enableCardPayment) ||
                      (!paymentSettings.enableCashPayment && paymentSettings.enableCardPayment)
                        ? 'grid-cols-1 max-w-md mx-auto'
                        : 'grid-cols-2'
                    } gap-6`}
                  >
                    {paymentSettings.enableCashPayment && (
                      <button
                        onClick={() => handlePaymentMethodSelection('cash')}
                        disabled={isSubmitting}
                        className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                          paymentMethod === 'Paiement à la livraison'
                            ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                            : 'border-gray-200 hover:border-orange-200'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <Wallet size={24} className="text-orange-500" />
                        </div>
                        <div className="text-center">
                          <span className="font-medium text-gray-800 text-lg block">{t('Paiement à la livraison')}</span>
                          <span className="text-sm text-gray-500">{t('Payez au moment de la livraison')}</span>
                        </div>
                      </button>
                    )}
                    {paymentSettings.enableCardPayment && (
                      <button
                        onClick={() => handlePaymentMethodSelection('card')}
                        disabled={isSubmitting}
                        className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                          paymentMethod === 'Carte'
                            ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                            : 'border-gray-200 hover:border-orange-200'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <CreditCard size={24} className="text-orange-500" />
                        </div>
                        <div className="text-center">
                          <span className="font-medium text-gray-800 text-lg block">{t('Paiement par carte')}</span>
                          <span className="text-sm text-gray-500">{t('Payez maintenant par carte bancaire')}</span>
                        </div>
                      </button>
                    )}
                  </div>
                  {showPaymentProcessor && (
                    <div className="mt-6 p-6 bg-gray-50 rounded-2xl">
                      <StripePaymentForm
                        amount={adjustedTotal !== null ? adjustedTotal : total}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        onCancel={handlePaymentCancel}
                      />
                    </div>
                  )}
                  {submitError && (
                    <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-center gap-2 text-red-600">
                      <AlertCircle size={20} />
                      <p className="text-sm">{submitError}</p>
                      <button
                        onClick={() => {
                          setSubmitError(null);
                          handlePaymentMethodSelection('card');
                        }}
                        className="ml-4 py-1 px-3 rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                      >
                        {t('Réessayer')}
                      </button>
                    </div>
                  )}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handlePreviousStep}
                      disabled={isSubmitting}
                      className="flex-1 py-3 rounded-xl font-semibold text-orange-500 bg-orange-50 hover:bg-orange-100 transition-colors disabled:opacity-50"
                    >
                      {t('Retour')}
                    </button>
                    {submitError && (
                      <button
                        onClick={closeOrderModal}
                        disabled={isSubmitting}
                        className="flex-1 py-3 rounded-xl font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        {t('Annuler la commande')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
{showNoteKeyboard && activeNoteId && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1000 popup-overlay">
    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl p-4 relative">
              <button
                onClick={closeNoteKeyboard}
                className="absolute top-0 right-0 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
              >
                <X size={24} />
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Note spéciale')}</h2>
              <p className="text-gray-600 mb-6">{t('Ajoutez une note pour ce produit')}</p>
              <div className="relative">
                <textarea
                  value={itemNotes[activeNoteId] || ''}
                  readOnly
                  placeholder={t('Votre note...')}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring focus:ring-orange-200 transition-colors outline-none text-lg font-medium text-gray-800 bg-gray-50 resize-none"
                  rows={3}
                />
                <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                  {(itemNotes[activeNoteId] || '').length}/200
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {letterRows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex justify-center gap-1">
                    {row.map((letter) => (
                   <button
  key={letter}
  onClick={() => handleNoteKeyPress(letter)}
  className="w-8 h-8 rounded-lg text-sm font-semibold bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200 transition-all active:transform active:scale-95"
>
                        {letter}
                      </button>
                    ))}
                  </div>
                ))}
                <div className="flex justify-center gap-2 mt-2">
                  <button
                    onClick={() => handleNoteKeyPress('clear')}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all active:transform active:scale-95"
                  >
                    {t('Effacer')}
                  </button>
                  <button
                    onClick={() => handleNoteKeyPress('space')}
                    className="px-8 py-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200 transition-all active:transform active:scale-95"
                  >
                    {t('Espace')}
                  </button>
                  <button
                    onClick={() => handleNoteKeyPress('backspace')}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all active:transform active:scale-95"
                  >
                    <Delete size={20} />
                  </button>
                </div>
              </div>
              <button
                onClick={closeNoteKeyboard}
                className="w-full mt-6 py-3 rounded-xl font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
              >
                {t('Valider la note')}
              </button>
            </div>
          </div>
        )}

        {invoiceData && showConfirmation && (
          <OrderConfirmation
            isOpen={showConfirmation}
            onClose={handleConfirmationClose}
            orderType={orderType || 'takeaway'}
            items={items}
            customerName={orderType === 'delivery' ? complementaryInfo : customerName}
            tableNumber={orderType === 'dine-in' ? tableNumber : undefined}
            deliveryAddress={orderType === 'delivery' ? deliveryAddress : undefined}
            complementaryInfo={orderType === 'delivery' ? complementaryInfo : undefined}
            phoneNumber={orderType === 'delivery' ? phoneNumber : undefined}
            paymentMethod={paymentMethod || 'cash'}
            invoiceData={invoiceData}
            printSettings={printSettings}
          />
        )}
      </div>
    </Elements>
  );
};

export default Cart;